// crewbit-admin/src/lib/sessionToken.ts
//
// Session token generation + persistence for the X-Session-Token header pattern.
// Spec: docs/references/MONEY_MODULE_WIRING_v1.md §2.4.1.
//
// Lifecycle on admin web:
// - Generated as a UUID v4 the first time a tab loads, via crypto.randomUUID().
// - Persisted in sessionStorage. NOT localStorage — sessionStorage is per-tab,
//   localStorage is shared across all tabs of the same origin and would defeat
//   per-tab isolation for accountants juggling multiple client businesses across
//   tabs (their primary use case).
// - sessionStorage survives page reload but resets when the tab is closed. New
//   tabs generate fresh tokens, giving each tab isolated active-business context.
// - Bootstrap fallback via window.name handles the duplicate-tab-from-context-menu
//   case where sessionStorage is empty in the new tab; window.name is per-tab
//   and survives same-tab navigation.

const STORAGE_KEY = 'crewbit-session-token';
const WINDOW_NAME_PREFIX = 'crewbit-session-token:';

let cachedToken: string | null = null;

/**
 * Returns the persisted session token, generating + persisting one if it doesn't exist.
 * Synchronous (sessionStorage is sync) — safe to call from module-load contexts including
 * Supabase client initialization.
 */
export function getOrCreateSessionToken(): string {
  if (cachedToken) return cachedToken;

  const existing = sessionStorage.getItem(STORAGE_KEY);
  if (existing) {
    cachedToken = existing;
    return existing;
  }

  // Bootstrap from window.name if a parent tab handed off via that channel.
  // Format: "crewbit-session-token:<uuid>". Defensive — only accept the prefix.
  if (typeof window !== 'undefined' && typeof window.name === 'string'
      && window.name.startsWith(WINDOW_NAME_PREFIX)) {
    const fromWindowName = window.name.slice(WINDOW_NAME_PREFIX.length);
    if (fromWindowName.length >= 16) {
      sessionStorage.setItem(STORAGE_KEY, fromWindowName);
      cachedToken = fromWindowName;
      return fromWindowName;
    }
  }

  const fresh = crypto.randomUUID();
  sessionStorage.setItem(STORAGE_KEY, fresh);
  cachedToken = fresh;
  return fresh;
}

/**
 * Synchronous read of the cached token. Returns null only if getOrCreateSessionToken
 * has never been called in this tab (impossible if the supabase client is initialized
 * via lib/supabase.ts, which calls it at module-load time).
 */
export function getSessionTokenSync(): string | null {
  return cachedToken;
}

/**
 * Test-only: clear the in-memory cache. Does NOT touch sessionStorage.
 */
export function __resetSessionTokenCacheForTesting(): void {
  cachedToken = null;
}
